'use client'

import clsx from 'clsx'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { FC, useCallback } from 'react'

import { usePaginationRelLinks } from '@/shared/lib/hooks/usePaginationRelLinks'
import { Button } from '@/shared/ui'
import { Icon } from '@/shared/ui/icon'

import { buildPageHref } from '../lib/build-page-href'
import { ELLIPSIS, getPaginationRange, PaginationItem } from '../lib/get-pagination-range'

import cls from './pagination-new.module.scss'

export interface PaginationNewProps {
	currentPage: number
	lastPage: number
	siblingCount?: number
	showMoreButton?: boolean
	loading?: boolean
	withUrlParam?: boolean
	withRelLinks?: boolean
	scrollToTop?: boolean
	shallow?: boolean
	onChange?: (page: number) => void
	onMoreClick?: (page: number) => void
}

const ICON_COLOR_DISABLED = '#1F22273D'
const ICON_COLOR_ENABLED = '#1F2227'

export const PaginationNew: FC<PaginationNewProps> = ({
	currentPage,
	lastPage,
	siblingCount = 1,
	showMoreButton = false,
	loading = false,
	withUrlParam = false,
	withRelLinks = true,
	scrollToTop = true,
	shallow = true,
	onChange,
	onMoreClick
}) => {
	const router = useRouter()
	const pathname = usePathname()
	const searchParams = useSearchParams()

	const isFirstPage = currentPage <= 1
	const isLastPage = currentPage >= lastPage

	const paginationRange = getPaginationRange({ currentPage, lastPage, siblingCount })

	const getHref = useCallback(
		(page: number) => buildPageHref({ page, pathname, searchParams }),
		[pathname, searchParams]
	)

	const navigateToPage = useCallback(
		async (page: number, shouldScrollToTop = false) => {
			if (page < 1 || page > lastPage || page === currentPage) return

			onChange?.(page)

			if (withUrlParam) {
				const href = getHref(page)
				await router.push(href)
			}

			if (shouldScrollToTop) {
				window.scrollTo({ top: 0, behavior: 'smooth' })
			}
		},
		[currentPage, getHref, lastPage, onChange, router, shallow, withUrlParam]
	)

	const handlePageClick = (e: React.MouseEvent<HTMLAnchorElement>, page: number) => {
		e.preventDefault()
		navigateToPage(page, scrollToTop)
	}

	const handleNavClick = (direction: 'prev' | 'next') => {
		const nextPage = direction === 'prev' ? currentPage - 1 : currentPage + 1
		navigateToPage(nextPage, scrollToTop)
	}

	const handleMoreClick = () => {
		onMoreClick?.(currentPage + 1)
	}

	const renderPageItem = (item: PaginationItem, index: number) => {
		if (item === ELLIPSIS) {
			return (
				<span key={`ellipsis-${index}`} className={clsx(cls.page, cls['page--ellipsis'])}>
					{ELLIPSIS}
				</span>
			)
		}

		const isActive = item === currentPage

		return (
			<a
				key={item}
				href={getHref(item)}
				className={clsx(cls.page, isActive && cls['page--active'])}
				onClick={e => handlePageClick(e, item)}
				aria-current={isActive ? 'page' : undefined}>
				{item}
			</a>
		)
	}

	const prevHref = getHref(currentPage - 1)
	const nextHref = getHref(currentPage + 1)

	usePaginationRelLinks({
		prevHref: withRelLinks ? prevHref : undefined,
		nextHref: withRelLinks ? nextHref : undefined,
		isFirstPage: !withRelLinks || isFirstPage,
		isLastPage: !withRelLinks || isLastPage
	})

	if (lastPage <= 1) return null

	return (
		<>
			<div className={cls.root}>
				{showMoreButton && !isLastPage && (
					<Button theme="outlined" isLoading={loading} className={cls.moreButton} onClick={handleMoreClick}>
						Показать ещё
					</Button>
				)}

				<div className={cls.pages}>
					<button
						type="button"
						className={cls.nav}
						disabled={isFirstPage}
						onClick={() => handleNavClick('prev')}
						aria-label="Предыдущая страница">
						<Icon name="ArrowLeft" size={16} color={isFirstPage ? ICON_COLOR_DISABLED : ICON_COLOR_ENABLED} />
					</button>

					{paginationRange.map(renderPageItem)}

					<button
						type="button"
						className={cls.nav}
						disabled={isLastPage}
						onClick={() => handleNavClick('next')}
						aria-label="Следующая страница">
						<Icon name="ArrowRight" size={16} color={isLastPage ? ICON_COLOR_DISABLED : ICON_COLOR_ENABLED} />
					</button>
				</div>
			</div>
		</>
	)
}
